import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  getAmenitiesEndpoint,
  createAmenityEndpoint,
  updateAmenityEndpoint,
} from '../services/projectService';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { ErrorAlert, SuccessAlert } from '../../../models/alertModel';
import { useNavigate } from 'react-router-dom';

const useProjectAmenity = () => {
  const navigate = useNavigate();
  const [sweetAlert, setSweetAlert] = useState<boolean>(false);
  const [alertProps, setAlertProps] = useState<SuccessAlert | ErrorAlert>({
    success: true,
    title: '',
    text: '',
    onConfirm: () => setSweetAlert(false),
    onCancel: () => setSweetAlert(false),
  });

  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [results, setResults] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { callEndpoint, isLoading } = useCallApiAndLoad();

  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      description: '',
    },
    resolver: yupResolver(
      yup
        .object({
          description: yup.string().required('Descripción es requerida'),
        })
        .required(),
    ),
  });

  const [searchQuery, setSearchQuery] = useState('');

  const filteredResults = results.filter((result) =>
    result.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const onSubmit = async (data: { description: string }) => {
    if (editingId !== null) {
      const { status } = await callEndpoint(
        updateAmenityEndpoint(editingId, data.description, token),
      );
      if (status !== 200) {
        setAlertProps((state) => ({
          ...state,
          success: false,
          danger: true,
          title: 'Error',
          text: 'Lo sentimos, la amenidad no pudo ser actualizada',
        }));
        setSweetAlert(true);
        return;
      }
      setEditingId(null);
      setResults((prevResults) =>
        prevResults.map((result) =>
          result.id === editingId ? { ...result, description: data.description } : result,
        ),
      );
      setAlertProps((state) => ({
        ...state,
        success: true,
        danger: false,
        title: 'OK',
        text: 'Amenidad actualizada correctamente',
      }));
      setSweetAlert(true);
      reset();
    } else {
      const { status } = await callEndpoint(
        createAmenityEndpoint(data.description, token),
      );
      if (status !== 201) {
        setAlertProps((state) => ({
          ...state,
          success: false,
          danger: true,
          title: 'Error',
          text: 'Lo sentimos, la amenidad no pudo ser creada',
        }));
        setSweetAlert(true);
        return;
      }
      setAlertProps((state) => ({
        ...state,
        onConfirm: () => navigate(0),
        success: true,
        danger: false,
        title: 'OK',
        text: 'Amenidad creada correctamente',
      }));
      setSweetAlert(true);
    }
  };

  // Handle edit button click
  const handleEdit = (id: string) => {
    if (results && results.length === 0) return;
    const resultToEdit = results.find((result) => result.id === id);
    if (resultToEdit) {
      setValue('description', resultToEdit.description);
      setEditingId(id);
    }
  };

  const loadAmenities = async () => {
    const { status, data } = await callEndpoint(getAmenitiesEndpoint(token));
    if (status !== 200) {
      return;
    }
    const response = fetchResponseAdapter(data);
    setResults(response.data);
  };

  useEffect(() => {
    loadAmenities().catch((e) => console.error(e));
  }, []);

  return {
    results,
    handleEdit,
    handleSubmit,
    onSubmit,
    control,
    errors,
    editingId,
    reset,
    setEditingId,
    sweetAlert,
    alertProps,
    isLoading,
    searchQuery,
    setSearchQuery,
    filteredResults,
  };
};

export default useProjectAmenity;
